import React, { FC, Fragment, ReactNode } from 'react';
import { clone } from 'lodash';

import { UserGroup } from 'types/app';
import { Content as ContentType, GetFeedDocument, useOnMediaStatusSubscription } from 'api';
import { Dimensions, Loaders, Queries } from 'components';
import { DimensionsProvider, QueryParamsProvider } from 'providers';
import { contentModel } from 'apollo/reducers';
import { useCurrentUser } from 'hooks';
import { NULL } from 'constants/index';

import Loader from '../components/Loader';
import PageHandler from '../components/PageHandler';

interface IProps {
  emptyState: () => JSX.Element;
  header?: ReactNode;
  selectedGroup?: UserGroup;
  variables: {
    sort: boolean;
    key: string;
    id: string;
  };
}

interface IChunkedResult {
  results: ContentType[];
  startIndex: number;
  endIndex: number;
}

const FEED_LIMIT = 10;
const LOADER_HEIGHT = 30;

const FeedManager: FC<IProps> = props => {
  const { emptyState, header, selectedGroup, variables } = props;
  const currentUser = useCurrentUser();

  const queryParams = {
    query: GetFeedDocument,
    variables: {
      limit: FEED_LIMIT,
      ...variables,
    },
  };

  useOnMediaStatusSubscription({
    variables: { user_id: currentUser.id },
    onSubscriptionData: ({ client, subscriptionData }) => {
      const { onMediaStatus } = subscriptionData.data;
      const { content_id } = onMediaStatus;

      contentModel({
        contentId: content_id,
        cache: client.cache,
        result: subscriptionData,
        ...queryParams,
      });
    },
  });

  function buildChunks(results: ContentType[]) {
    const clonedResults = clone(results);
    const chunkedResults: IChunkedResult[] = [];
    let startIndex = 0;

    while (clonedResults.length) {
      const chunk = clonedResults.splice(0, FEED_LIMIT);
      const endIndex = startIndex + chunk.length;

      chunkedResults.push({
        results: chunk,
        startIndex,
        endIndex,
      });

      startIndex = endIndex + 1;
    }

    return chunkedResults;
  }

  function renderLoadMoreLoader(loading: boolean) {
    if (loading) {
      return <Loaders.Standard display="block" height={LOADER_HEIGHT} />;
    }

    return <div style={{ height: LOADER_HEIGHT }} />;
  }

  return (
    <QueryParamsProvider value={queryParams}>
      {header}
      <Queries.GetFeed loader={<Loader />} {...queryParams}>
        {(getFeed, { loading, onLoadMore }) => {
          const chunkedResults = buildChunks(getFeed.results);

          if (!chunkedResults.length) {
            return emptyState();
          }

          return (
            <Fragment>
              <PageHandler
                chunkedResults={chunkedResults}
                selectedGroup={selectedGroup}
                onLoadMoreRows={onLoadMore}
              />
              {renderLoadMoreLoader(loading)}
            </Fragment>
          );
        }}
      </Queries.GetFeed>
    </QueryParamsProvider>
  );
};

FeedManager.defaultProps = {
  header: NULL,
  selectedGroup: NULL,
};

const FeedWrapper: FC<IProps> = props => (
  <Dimensions>
    {dimensions => (
      <DimensionsProvider value={{ dimensions }}>
        <FeedManager {...props} />
      </DimensionsProvider>
    )}
  </Dimensions>
);

export default FeedWrapper;
